import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarDays,
  Clock3,
  Phone,
  Mail,
  MapPin,
  User,
  Stethoscope,
  Building2,
  ShieldCheck
} from 'lucide-react';
import toast from 'react-hot-toast';
import './styles/appointments.css';
import PageHeader from './ui/PageHeader';
import bookingPlanService from '../../core/services/bookingplanService';
const STATUS_CONFIG = {
  Active: 'badge-green',
  Scheduled: 'badge-green',
  Completed: 'badge-amber',
  Cancelled: 'badge-red',
  Modified: 'badge-amber',
  AVAILABLE: 'badge-green',
  'ON LEAVE': 'badge-amber',
  'NOT AVAILABLE': 'badge-gray'
};

const getValue = (...values) => {
  const value = values.find(v => v !== undefined && v !== null && v !== '');
  return value === undefined ? '-' : value;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatTime = (value) => {
  if (!value) return '-';
  return String(value).slice(0, 5);
};

const getProviderDisplay = (record) => {
  if (!record) return '-';

  const isCounsellor = String(record.type || record.doctor_type || record.doctorType || '')
    .toLowerCase() === 'counsellor';

  if (isCounsellor) {
    if (record.counselor_name) return record.counselor_name;
    if (record.doctor_name) return record.doctor_name;
    if (record.doctor_identifier || record.doctor_type) {
      return [record.doctor_identifier, record.doctor_type].filter(Boolean).join(' - ');
    }
    return getValue(
      record.counselor_name,
      `${record.displaytitle || ''} ${record.first_name || ''} ${record.last_name || ''}`.trim()
    );
  }

  if (record.doctor_name) return record.doctor_name;
  if (record.doctor_identifier || record.doctor_type) {
    return [record.doctor_identifier, record.doctor_type].filter(Boolean).join(' - ');
  }
  return getValue(
    record.doctor_name,
    `${record.displaytitle || ''} ${record.first_name || ''} ${record.last_name || ''}`.trim()
  );
};

const getProviderType = (record) => {
  if (!record) return '-';
  return getValue(record.type, record.doctor_type, record.doctorType, record.specialization, record.department);
};

const DetailItem = ({ label, value, icon }) => (
  <div className="detail-item">
    <div className="detail-label">
      {icon}
      <span>{label}</span>
    </div>
    <strong>{getValue(value)}</strong>
  </div>
);

const DetailSection = ({ title, children }) => (
  <section className="detail-section">
    <div className="section-header">
      <h2>{title}</h2>
    </div>
    <div className="detail-grid">{children}</div>
  </section>
);

export default function BookingRecordView({ type }) {
  const location = useLocation();
  const history = useHistory();

  // Get type from route props, location state, or default to 'plan'
  const resolvedType = type || location.state?.type || 'plan';
  const id = location.state?.id || location.pathname.split('/').pop();

  const [record, setRecord] = useState(location.state?.record || null);
  const [loading, setLoading] = useState(true);

  const isPlan = resolvedType === 'plan';
  const isPatient = resolvedType === 'patient';
  const isDoctor = resolvedType === 'doctor';
  const isSession = resolvedType === 'session';

  const backPath = isPlan
    ? '/booking-plans'
    : isPatient
      ? '/patients'
      : isDoctor
        ? '/doctors'
        : '/sessions';

  useEffect(() => {
    const fetchRecord = async () => {
  setLoading(true);

  try {
    let response;

    if (isPlan) {
      response = await bookingPlanService.getBookingPlanById(id);
    } else if (isPatient) {
      response = await bookingPlanService.getPatientById(id);
    } else if (isDoctor) {
if (isDoctor) {
  response = await bookingPlanService.getDoctorById(id);
}    } else {
      response = await bookingPlanService.getSessionById(id);
    }

    const data = response?.data || response;

    setRecord(data?.data || data);
  } catch (error) {
    console.error(error);
    toast.error('Failed to load details');
  } finally {
    setLoading(false);
  }
};
    if (!record) fetchRecord();
  }, [id, resolvedType, isPlan, isPatient, isDoctor, isSession]);

  const view = useMemo(() => {
    const sessionNumber = getValue(record?.session_number, record?.day_number, 1);
    const totalSessions = getValue(record?.total_sessions, record?.total_days, 1);

    const date = getValue(record?.session_date, record?.start_date, record?.dob);
    const startTime = getValue(record?.start_time);
    const endTime = getValue(record?.end_time);

    let status = getValue(
      record?.session_status,
      record?.status,
      record?.booking_status
    );

    if (isDoctor) {
      if (status === 'AVAILABLE') status = 'Available';
      else if (status === 'ON LEAVE') status = 'On Leave';
      else if (status === 'NOT AVAILABLE') status = 'Not Available';
    }

    let title = 'Record Details';
    if (isPlan) title = 'Booking Plan Details';
    if (isPatient) title = 'Patient Profile';
    if (isDoctor) title = 'Doctor Profile';
    if (isSession) title = 'Session Details';

    return {
      title,
      subtitle: getValue(record?.booking_code, record?.patientId, record?.regno, `Record #${id}`),
      patientName: isDoctor
        ? getValue(`${record?.displaytitle || ''} ${record?.first_name || ''} ${record?.last_name || ''}`)
        : getValue(record?.patient_name, record?.fname ? `${record.fname} ${record.lname || ''}` : null),
      code: getValue(record?.booking_code, record?.patientId, record?.regno, `#${id}`),
      status,
      statusClass: STATUS_CONFIG[status] || STATUS_CONFIG[record?.status] || 'badge-gray',
      sessionText: `${sessionNumber}/${totalSessions}`,
      date,
      timeText: `${formatTime(startTime)} - ${formatTime(endTime)}`
    };
  }, [record, id, isPlan, isPatient, isDoctor, isSession])

  if (loading && !record) {
    return (
      <div className="record-view-page">
        <div className="record-loading">
          <div className="spinner" />
          <p>Loading details...</p>
        </div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="record-view-page">
        <div className="empty-state">
          <h2>Record not found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container fade-in">

      {/* HEADER */}
      <PageHeader
        eyebrow="Profile Overview"
        title={view.title}
        subtitle={view.subtitle}
        onBack={() => history.push(backPath)}
      />

      {/* HERO */}
      <div className="record-summary">
        <div className="summary-left">

          <div className="record-avatar-large">
            {isPatient ? (
              <img
                src={
                  record.imgurl || record.imgUrl
                    ? `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${record.imgurl || record.imgUrl}`
                    : `https://ui-avatars.com/api/?name=${encodeURIComponent(view.patientName)}&background=2563eb&color=fff`
                }
                alt="profile"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(view.patientName)}&background=2563eb&color=fff`;
                }}
              />
            ) : (
              <div className="avatar-fallback">
                {view.patientName?.charAt(0)}
              </div>
            )}
          </div>

          <div>
            <span className="summary-label">
              {isDoctor ? 'Doctor Profile' : 'Patient Profile'}
            </span>

            <h2>{view.patientName}</h2>
            <p>{view.code}</p>

            <div className="summary-inline">
              <span>
                <CalendarDays size={15} />
                {formatDate(view.date)}
              </span>

              {(isPlan || isSession) && (
                <span>
                  <Clock3 size={15} />
                  {view.timeText}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="summary-meta">
          <span className={`badge ${view.statusClass}`}>{view.status}</span>

          {(isPlan || isSession) && (
            <span className="badge badge-blue">
              Session {view.sessionText}
            </span>
          )}
        </div>
      </div>

      {/* DETAILS */}

      <div className="detail-layout">
        {(isPatient || isPlan || isSession) && (
          <>
            <DetailSection title="Patient Information">
              <DetailItem
                label="First Name"
                value={record.fname}
                icon={<User size={14} />}
              />

              <DetailItem
                label="Middle Name"
                value={record.mname}
                icon={<User size={14} />}
              />

              <DetailItem
                label="Last Name"
                value={record.lname}
                icon={<User size={14} />}
              />

              <DetailItem
                label="Patient ID"
                value={getValue(
                  record.patient_id,
                  record.patientId,
                  record.pid
                )}
                icon={<ShieldCheck size={14} />}
              />

              <DetailItem
                label="Gender"
                value={record.gender}
                icon={<User size={14} />}
              />

              <DetailItem
                label="Age"
                value={record.age}
                icon={<User size={14} />}
              />

              <DetailItem
                label="DOB"
                value={formatDate(record.dob)}
                icon={<CalendarDays size={14} />}
              />

              <DetailItem
                label="Blood Group"
                value={getValue(
                  record.bloodgroup,
                  record.blood_group
                )}
                icon={<ShieldCheck size={14} />}
              />
            </DetailSection>

            <DetailSection title="Contact Information">
              <DetailItem
                label="Phone"
                value={record.phno}
                icon={<Phone size={14} />}
              />

              <DetailItem
                label="Alternative Phone"
                value={record.altPhno}
                icon={<Phone size={14} />}
              />

              <DetailItem
                label="Emergency Phone"
                value={record.emrPhono}
                icon={<Phone size={14} />}
              />

              <DetailItem
                label="Email"
                value={record.email}
                icon={<Mail size={14} />}
              />

              <DetailItem
                label="Address"
                value={record.address}
                icon={<MapPin size={14} />}
              />

              <DetailItem
                label="City"
                value={record.city}
                icon={<MapPin size={14} />}
              />

              <DetailItem
                label="State"
                value={record.state}
                icon={<MapPin size={14} />}
              />

              <DetailItem
                label="Pincode"
                value={record.pincode}
                icon={<MapPin size={14} />}
              />
            </DetailSection>
          </>
        )}

        {(isDoctor || isPlan || isSession) && (
          <>
            <DetailSection title="Doctor / Counsellor Information">
              <DetailItem
                label="Provider"
                value={getProviderDisplay(record)}
                icon={<Stethoscope size={14} />}
              />

              <DetailItem
                label="GV Identifier"
                value={record.doctor_identifier || record.gvId || record.gvid}
                icon={<ShieldCheck size={14} />}
              />

              <DetailItem
                label="GV Type"
                value={getProviderType(record)}
                icon={<Building2 size={14} />}
              />

              <DetailItem
                label="Specialization"
                value={record.specialization}
                icon={<Stethoscope size={14} />}
              />

              <DetailItem
                label="Qualification"
                value={record.qualification}
                icon={<ShieldCheck size={14} />}
              />

              <DetailItem
                label="Registration No"
                value={record.regno}
                icon={<ShieldCheck size={14} />}
              />

              <DetailItem
                label="Phone"
                value={record.phone}
                icon={<Phone size={14} />}
              />

              <DetailItem
                label="Email"
                value={record.email}
                icon={<Mail size={14} />}
              />

              <DetailItem
                label="Working Shift"
                value={`${formatTime(
                  record.start_time
                )} - ${formatTime(record.end_time)}`}
                icon={<Clock3 size={14} />}
              />
            </DetailSection>
          </>
        )}

        {(isPlan || isSession) && (
          <>
            <DetailSection title="Room Information">
              <DetailItem
                label="Room"
                value={getValue(
                  record.room_number,
                  record.roomName,
                  record.room_name
                )}
                icon={<Building2 size={14} />}
              />

              <DetailItem
                label="Room Code"
                value={getValue(
                  record.room_code,
                  record.roomCode
                )}
                icon={<Building2 size={14} />}
              />

              <DetailItem
                label="Room Type"
                value={getValue(
                  record.room_type,
                  record.type
                )}
                icon={<Building2 size={14} />}
              />

              <DetailItem
                label="Floor"
                value={record.floorname || record.floorName}
                icon={<Building2 size={14} />}
              />
            </DetailSection>

            <DetailSection title="Treatment Details">
              <DetailItem
                label="Package"
                value={record.package_name}
                icon={<Stethoscope size={14} />}
              />

              <DetailItem
                label="Service"
                value={record.package_service_name}
                icon={<Stethoscope size={14} />}
              />

              <DetailItem
                label="Medicine / Oil"
                value={record.medicine_oil}
                icon={<Stethoscope size={14} />}
              />

              <DetailItem
                label="Booking Mode"
                value={record.booking_mode}
                icon={<CalendarDays size={14} />}
              />

              <DetailItem
                label="Booking Status"
                value={getValue(
                  record.booking_status,
                  record.status
                )}
                icon={<ShieldCheck size={14} />}
              />

              <DetailItem
                label="Notes"
                value={getValue(
                  record.notes,
                  record.session_notes
                )}
                icon={<Stethoscope size={14} />}
              />
            </DetailSection>

            {(record.doctor_id2 || record.package_id2 || record.package_service_id2 ||
              record.medicine_oil2 || record.notes2 || record.treatment_name2) && (
              <DetailSection title="Treatment Details (2)">
                <DetailItem
                  label="Doctor / Counsellor"
                  value={getValue(record.doctor_name2, record.doctor_identifier2)}
                  icon={<Stethoscope size={14} />}
                />

                <DetailItem
                  label="Package"
                  value={record.package_name2}
                  icon={<Stethoscope size={14} />}
                />

                <DetailItem
                  label="Service"
                  value={record.package_service_name2}
                  icon={<Stethoscope size={14} />}
                />

                <DetailItem
                  label="Medicine / Oil"
                  value={record.medicine_oil2}
                  icon={<Stethoscope size={14} />}
                />

                <DetailItem
                  label="Sessions"
                  value={record.total_sessions2}
                  icon={<CalendarDays size={14} />}
                />

                <DetailItem
                  label="Notes"
                  value={record.notes2}
                  icon={<Stethoscope size={14} />}
                />
              </DetailSection>
            )}
          </>
        )}
      </div>

      {/* Styles are in ./styles/appointments.css */}
    </div>
  );
}
